import { CredentialType } from "./enums";

/**
 * Types whose `details` carry signing provenance for the whole answer set.
 * A field-selective blend would keep `signedAt`/`method`/`vendorRef` beside
 * answers the provider never signed, so these import only as one complete
 * version. Shared so the client hides checkboxes the server would reject.
 */
export const WHOLE_VERSION_IMPORT_TYPES: readonly CredentialType[] = [
  CredentialType.Attestation,
];

export function supportsCredentialImportFieldSelection(
  type: CredentialType,
): boolean {
  return !WHOLE_VERSION_IMPORT_TYPES.includes(type);
}

export type CredentialImportVersion = {
  number?: string | null;
  stateCode?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  status?: string | null;
  details?: unknown;
};

export type CredentialImportField = {
  path: string;
  before: unknown;
  after: unknown;
  changed: boolean;
};

type FieldValues = Omit<CredentialImportField, "changed">;
const PROMOTED_FIELDS = [
  "number",
  "stateCode",
  "issuedAt",
  "expiresAt",
  "status",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function canonicalValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalValue);
  }
  if (!isRecord(value)) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, item]) => [key, canonicalValue(item)]),
  );
}

function sameValue(before: unknown, after: unknown): boolean {
  return (
    JSON.stringify(canonicalValue(before)) ===
    JSON.stringify(canonicalValue(after))
  );
}

function ownValue(value: unknown, key: string): unknown {
  if (!isRecord(value) || !Object.hasOwn(value, key)) {
    return undefined;
  }
  return value[key];
}

function childFields({ before, after, path }: FieldValues): FieldValues[] {
  // Lists are one field: selecting array indices could produce sparse arrays or
  // combine unrelated entries after a reorder. Structural type changes are also
  // atomic; ordinary nested objects still allow selection of individual values.
  if (
    (before !== undefined && !isRecord(before)) ||
    (after !== undefined && !isRecord(after))
  ) {
    return [];
  }
  const keys = new Set([
    ...Object.keys(isRecord(before) ? before : {}),
    ...Object.keys(isRecord(after) ? after : {}),
  ]);
  return [...keys].map((key) => ({
    // Escape actual separators so a stored key containing a dot cannot collide
    // with a nested path. Paths are compared, never evaluated as property access.
    path: `${path}.${encodeURIComponent(key).replaceAll(".", "%2E")}`,
    before: ownValue(before, key),
    after: ownValue(after, key),
  }));
}

function collectFields(values: FieldValues): CredentialImportField[] {
  const children = childFields(values);
  if (children.length > 0) {
    return children.flatMap(collectFields);
  }
  return [{ ...values, changed: !sameValue(values.before, values.after) }];
}

function versionFields({
  sourceVersion,
  targetVersion,
}: {
  sourceVersion: CredentialImportVersion;
  targetVersion: CredentialImportVersion | null;
}): FieldValues[] {
  return [
    ...PROMOTED_FIELDS.map((path) => ({
      path,
      before: targetVersion?.[path] ?? null,
      after: sourceVersion[path] ?? null,
    })),
    {
      path: "details",
      before: targetVersion?.details ?? {},
      after: sourceVersion.details ?? {},
    },
  ];
}

/** Shared by review and import so checkboxes name exactly the values the server
 * can adopt. Provenance, IDs and document permissions are never selectable. */
export function credentialImportFields(input: {
  sourceVersion: CredentialImportVersion;
  targetVersion: CredentialImportVersion | null;
}): CredentialImportField[] {
  return versionFields(input)
    .flatMap(collectFields)
    .filter((field) => field.path !== "details" || field.changed);
}

function mergeField({
  values,
  selected,
}: {
  values: FieldValues;
  selected: Set<string>;
}): unknown {
  const { before, after, path } = values;
  if (sameValue(before, after)) {
    return before;
  }
  if (selected.has(path)) {
    return after;
  }
  const children = childFields(values);
  if (children.length === 0) {
    return before;
  }
  const merged = Object.fromEntries(
    children.flatMap((child) => {
      const value = mergeField({ values: child, selected });
      if (value === undefined) {
        return [];
      }
      const key = decodeURIComponent(child.path.slice(path.length + 1));
      return [[key, value]];
    }),
  );
  // Do not leave an empty parent when its last child was removed, or create one
  // for an entirely unchecked new group. An explicitly offered {} is retained.
  if (
    Object.keys(merged).length === 0 &&
    (before === undefined || after === undefined)
  ) {
    return undefined;
  }
  return merged;
}

/** Derives values only from the reviewed snapshots. Callers validate selections
 * against credentialImportFields before writing; no client values are trusted. */
export function selectCredentialImportFields({
  sourceVersion,
  targetVersion,
  selectedFieldPaths,
}: {
  sourceVersion: CredentialImportVersion;
  targetVersion: CredentialImportVersion | null;
  selectedFieldPaths: string[];
}): CredentialImportVersion {
  const selected = new Set(selectedFieldPaths);
  return Object.fromEntries(
    versionFields({ sourceVersion, targetVersion }).map((values) => [
      values.path,
      mergeField({ values, selected }),
    ]),
  );
}
